<template>
    <div class="face-recognition-container">
        <div class="ai-face-detection-container" v-if="!loggedSuccessfully">
            <faceVideoComp :portrait="true" v-if="!isCameraAndroid() && !pauseDetection"></faceVideoComp>
            <img class="image-captured" :src="'data:image/jpeg;base64,' + imageBase64" v-if="imageBase64" />
        </div>

        <div class="message-container" v-if="!loggedSuccessfully">
            <p v-if="isProcessingRequest" class="info" v-text="getString('aguarde_enquanto_realizamos_o_login')"></p>
            <p v-else-if="faceNotDetected" class="warning" v-text="getString('rosto_nao_detectado')"></p>
            <p v-else-if="noFaceRecognized && !pauseDetection" class="error" v-text="getString('rosto_nao_reconhecido')"></p>
            <p v-else-if="pauseDetection" class="error" v-text="getString('reconhecimento_facial_pausado')"></p>
            <p v-else class="info" v-text="getString('aguardando_reconhecimento')"></p>
        </div>

        <template v-if="loggedSuccessfully">
            <div class="user-verification-container">
                <div class="user-verification-content">
                    <img src="https://www.freeiconspng.com/uploads/clipart--person-icon--cliparts-15.png">
                    <h1>{{ getString('continuar_como') }} {{ consumerName }}?</h1>
                    <DefaultButton :text="getString('continuar')" @defaultBtnClick="loginSuccess()">
                    </DefaultButton>
                </div>
                <div class="change-method-container">
                    <h2 v-text="getString('nao_e_voce')"></h2>
                    <div class="change-method-buttons">
                        <DefaultButton :text="getString('tentar_novamente')"
                            @defaultBtnClick="resetUserVerification()">
                        </DefaultButton>
                        <DefaultButton :text="getString('tentar_outro_metodo')"
                            @defaultBtnClick="changeMethod()">
                        </DefaultButton>
                    </div>
                </div>
            </div>
        </template>

        <div class="controls-buttons" v-if="!loggedSuccessfully">
            <DefaultButton v-if="pauseDetection && !isProcessingRequest"
                :text="getString('tentar_novamente')"
                @defaultBtnClick="resetAttempts()">
            </DefaultButton>
            <DefaultButton :text="getCancelText()" :outlined="true"
                @defaultBtnClick="cancelLogin()">
            </DefaultButton>
            <DefaultButton :text="getString('tentar_outro_metodo')" :outlined="true"
                @defaultBtnClick="changeMethod()">
            </DefaultButton>
        </div>
    </div>
</template>

<script>
import Util from '@/assets/libs/util'
import eventModule from '@/assets/libs/eventModule'
import DefaultButton from '@/components/DefaultButton'
import IdentifiedConsumerController from '@/assets/controllers/IdentifiedConsumerController'
import LocaleController from '@/assets/controllers/LocaleController'
import faceVideoComp from '@/components/AIFaceVideoComponent.vue'

export default {
    name: 'ConsumerLoginFaceRecognition',
    components: {
        DefaultButton,
        faceVideoComp
    },
    data () {
        return {
            rawConsumer: null,
            consumer: null,
            controlsEnabled: true,
            loggedSuccessfully: false,
            consumerName: null,
            isProcessingRequest: false,
            noFaceRecognized: false,
            faceNotDetected: false,
            pauseDetection: false,
            androidAttempts: 0,
            attempts: 0,
            paramsFaceRecognition: Util.getFaceRecognitionParams(),
            imageBase64: null
        }
    },
    watch: {
        pauseDetection () {
            eventModule.$emit('update:pauseDetection', this.pauseDetection)
        }
    },
    created () {
        eventModule.$on('faceDetected', this.handleFaceDetectedEvent)
        eventModule.$on('cancelRec', this.cancelRecAndroid)
    },
    mounted () {
        if (!this.paramsFaceRecognition || !this.paramsFaceRecognition.linkApi || !this.paramsFaceRecognition.colectionCode) {
            Util.showInfoModal(this.getString('erro_ao_carregar_reconhecimento_facial'))
            Util.logComunication('Face Recognition Params', this.paramsFaceRecognition, {})
            this.$emit('cancelLogin')
            return
        }
        if (this.isCameraAndroid()) {
            this.startCameraAndroid()
        }
    },
    beforeDestroy () {
        eventModule.$off('faceDetected', this.handleFaceDetectedEvent)
        eventModule.$off('cancelRec', this.cancelRecAndroid)
        if (this.isCameraAndroid()) {
            window.CameraIntegration.stopCamera()
        }
    },
    methods: {
        getString: function (key) {
            return LocaleController.getString(key)
        },
        isCameraAndroid: function () {
            return Util.isAndroidMode() && !!window.CameraIntegration
        },
        startCameraAndroid: function () {
            window.CameraIntegration.startCamera(false)
        },
        handleFaceDetectedEvent: function ({ imageBase64 }) {
            if (!this.pauseDetection && !this.isProcessingRequest) {
                this.resetRecognitionState()
                this.sendFaceRecognitionRequest(imageBase64)
            }
        },
        sendFaceRecognitionRequest: async function (imageBase64) {
            try {
                this.isProcessingRequest = true
                this.controlsEnabled = false

                if (this.isCameraAndroid()) {
                    window.CameraIntegration.pauseCamera()
                }

                const response = await fetch(this.paramsFaceRecognition.linkApi + 'recognize', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        collection_code: this.paramsFaceRecognition.colectionCode,
                        image: imageBase64
                    })
                })

                const result = await response.json()
                if (!result || (result && result.status_code !== 200)) {
                    this.faceNotDetected = true
                    this.pauseDetection = true
                    if (this.isCameraAndroid()) {
                        window.CameraIntegration.stopCamera()
                    }
                    Util.showInfoModal(result.message || this.getString('falha_reconhecimento_facial') + ' <br>' + this.getString('tente_outro_metodo'))
                    Util.logComunication('Face Recognition Result', result, {})
                    this.changeMethod()
                    return
                }
                if (result.data === null) {
                    this.faceNotDetected = true
                    if (this.isCameraAndroid()) {
                        window.CameraIntegration.resumeCamera()
                    }
                } else if (Array.isArray(result.data) && result.data.length === 0) {
                    this.noFaceRecognized = true
                    this.attempts++

                    if (this.attempts >= 3) {
                        this.pauseDetection = true
                        if (this.isCameraAndroid()) {
                            window.CameraIntegration.stopCamera()
                        }
                    }
                } else if (this.isValidResponse(result)) {
                    this.pauseDetection = true
                    this.rawConsumer = result.data[0]
                    await this.doFaceLogin()
                    if (this.isCameraAndroid()) {
                        window.CameraIntegration.stopCamera()
                    }
                }
            } catch (error) {
                console.error('Erro ao enviar requisição de reconhecimento facial:', error)
            } finally {
                this.isProcessingRequest = false
                this.controlsEnabled = true

                if (this.androidAttempts >= 10 && this.isCameraAndroid()) {
                    Util.showInfoModal(this.getString('falha_reconhecimento_facial'))
                    window.CameraIntegration.stopCamera()
                } else if (this.isCameraAndroid() && !this.pauseDetection) {
                    window.CameraIntegration.resumeCamera()
                }

                this.androidAttempts++
            }
        },
        isValidResponse: function (response) {
            return response.data && response.data.length > 0 &&
                response.data[0] && response.data[0] !== 'undefined'
        },
        doFaceLogin: async function () {
            let loginResponse = await IdentifiedConsumerController.loginWithFaceRecognition(this.rawConsumer)

            if (loginResponse && loginResponse.success) {
                let consumerData = IdentifiedConsumerController.getConsumerData()
                if (!consumerData.consumerName || typeof consumerData.consumerName !== 'string') {
                    consumerData.consumerName = ''
                }
                this.consumerName = consumerData.consumerName && consumerData.consumerName.length > 15 ? consumerData.consumerName.substring(0, 15) + '...' : consumerData.consumerName
                this.consumerName = this.consumerName.trim()
                this.loggedSuccessfully = true
            }
        },
        loginSuccess: function () {
            this.$emit('loginSuccess')
        },
        resetUserVerification: function () {
            this.loggedSuccessfully = false
            this.consumerName = null
            this.consumer = null
            this.rawConsumer = null
            this.imageBase64 = null
            IdentifiedConsumerController.userLogout()
            IdentifiedConsumerController.clearConsumerData()
            this.resetAttempts()
        },
        resetAttempts: function () {
            this.attempts = 0
            this.androidAttempts = 0
            this.resetRecognitionState()
            if (this.isCameraAndroid()) {
                this.startCameraAndroid()
            }
        },
        resetRecognitionState: function () {
            this.noFaceRecognized = false
            this.faceNotDetected = false
            this.pauseDetection = false
            this.imageBase64 = null
        },
        cancelLogin: function (error = false) {
            IdentifiedConsumerController.userLogout()
            IdentifiedConsumerController.clearConsumerData()
            this.$emit('cancelLogin', error)
        },
        cancelRecAndroid: async function () {
            do {
                await new Promise(resolve => setTimeout(resolve, 100))
            } while (!this.controlsEnabled)

            if (!this.loggedSuccessfully) {
                this.resetRecognitionState()
                this.cancelLogin(true)
            }
        },
        changeMethod: function () {
            IdentifiedConsumerController.userLogout()
            IdentifiedConsumerController.clearConsumerData()
            this.$emit('changeMethod', 'faceRecognition')
        },
        getCancelText: function () {
            return this.$router.currentRoute.name === 'Payment' ? this.getString('cancelar') : this.getString('nao_quero_identificar')
        }
    }
}
</script>

<style scoped lang='less'>
@import (reference) url("../../assets/common.less");

.face-recognition-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-evenly;
    height: 100%;
    width: 100%;

    .ai-face-detection-container {
        display: flex;
        justify-content: center;
        align-items: center;
        max-height: 730px;
        max-width: 60%;

        .image-captured {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: contain;
        }
    }

    .message-container {
        min-height: 50px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 20px;
        text-align: center;

        p {
            font-size: 28px;
            margin: 0;
            text-align: center;

            &.error {
                color: #d32f2f;
            }

            &.warning {
                color: #f57c00;
            }

            &.info {
                color: #1565c0;
            }
        }
    }

    .user-verification-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-evenly;
        width: 100%;
        height: 100%;
        overflow: hidden auto;

        .user-verification-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;

            img {
                display: block;
                margin: auto;
                width: 150px;
                height: 150px;
                border: solid 1px;
                border-radius: 100%;
                object-fit: contain;
                padding: 10px;
                margin-bottom: 30px;
            }

            h1 {
                font-size: 40px;
                margin: 50px 0;
            }

            button {
                width: 100%;
            }
        }

        .change-method-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            margin: 60px 0;
            width: 100%;

            h2 {
                font-size: 30px;
                margin-bottom: 30px;
            }

            .change-method-buttons {
                display: flex;
                flex-direction: row;
                justify-content: center;
                margin: 15px auto;
                width: 100%;

                button {
                    width: 40%;
                    max-width: unset;
                    margin: 0 10px;
                }
            }
        }
    }

    .controls-buttons {
        display: flex;
        flex-direction: row;
        justify-content: center;
        width: 100%;
        margin: 20px auto;

        button {
            width: 40%;
            margin: 0 10px;
        }
    }
}

@media (max-width: 698px) {
    .face-recognition-container {
        .ai-face-detection-container {
            max-width: 100%;
            height: 250px;
            margin: 5px auto;
        }

        .message-container {
            p {
                font-size: 16px;
            }
        }

        .user-verification-container {
            height: 90vh;
            margin: 50px 0;

            .user-verification-content {
                img {
                    width: 80px;
                    height: 80px;
                    padding: 5px;
                    margin-bottom: 15px;
                }

                h1 {
                    font-size: 20px;
                    margin: 20px 0;
                }
            }

            .change-method-container {
                margin: 20px 0;

                h2 {
                    font-size: 16px;
                    margin-bottom: 15px;
                }

                .change-method-buttons {
                    margin: 10px auto;

                    button {
                        width: 40%;
                        margin: 0 5px;
                    }
                }
            }
        }

        .controls-buttons {
            margin: 10px auto;

            button {
                width: 45%;
                margin: 0 5px;
            }
        }
    }
}

@media (min-width: 699px) and (max-width: 1025px) {
    .face-recognition-container {
        .ai-face-detection-container {
            max-width: 100%;
            height: 550px;
            margin: 15px auto;
        }

        .message-container {
            p {
                font-size: 22px;
            }
        }

        .user-verification-container {
            height: 90vh;
            margin: 80px 0;

            .user-verification-content {
                img {
                    width: 100px;
                    height: 100px;
                    padding: 8px;
                    margin-bottom: 20px;
                }

                h1 {
                    font-size: 28px;
                    margin: 35px 0;
                }
            }

            .change-method-container {
                margin: 40px 0;

                h2 {
                    font-size: 22px;
                    margin-bottom: 20px;
                }

                .change-method-buttons {
                    margin: 12px auto;

                    button {
                        width: 40%;
                        margin: 0 8px;
                    }
                }
            }
        }

        .controls-buttons {
            margin: 15px auto;
        }
    }
}

@media (orientation: landscape) {
    .face-recognition-container {
        .ai-face-detection-container {
            max-width: 100%;
            height: 350px;
            margin: 15px auto;
        }

        .message-container {
            p {
                font-size: 16px;
            }
        }

        .user-verification-container {
            height: 90vh;
            margin: 50px 0;

            .user-verification-content {
                img {
                    width: 80px;
                    height: 80px;
                    padding: 5px;
                    margin-bottom: 15px;
                }

                h1 {
                    font-size: 20px;
                    margin: 20px 0;
                }
            }

            .change-method-container {
                margin: 20px 0;

                h2 {
                    font-size: 16px;
                    margin-bottom: 15px;
                }

                .change-method-buttons {
                    margin: 10px auto;

                    button {
                        width: 40%;
                        margin: 0 5px;
                    }
                }
            }
        }

        .controls-buttons {
            margin: 10px auto;

            button {
                width: 40%;
                margin: 0 5px;
            }
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/components/consumerLogin/ConsumerLoginFaceRecognition.vue